if (!customElements.get("product-grid")) {
    customElements.define("product-grid", class extends HTMLElement {
        constructor() {
            super();
            this.debounceTimer = null;
        }

        // Simple debounce function
        debounce(func, delay) {
            clearTimeout(this.debounceTimer);
            this.debounceTimer = setTimeout(func, delay);
        }

        connectedCallback() {
            // Setup quick filter tabs to control category filters
            this.setupQuickFilterTabs();
            this.setupCollectionGridImages();

            // Setup POUCHTECH filter conditional visibility
            this.setupPouchTechFilter();

            // Filter drawer toggle functionality
            const filterToggleButton = this.querySelector('.product-grid-filter-toggle');
            const filterDrawer = this.querySelector('.custom-drawer');

            if (filterToggleButton && filterDrawer) {
                // Toggle drawer when filter button is clicked
                filterDrawer.style.display = '';
                filterToggleButton.addEventListener('click', () => {
                    filterDrawer.classList.add('active');
                    document.body.style.overflow = 'hidden'; // Prevent scrolling when drawer is open
                });

                // Close drawer when clicking outside or on close icon
                const closeDrawerButton = filterDrawer.querySelector('.icon-close, .custom-drawer-header .icon');
                if (closeDrawerButton) {
                    closeDrawerButton.addEventListener('click', (event) => {
                        event.preventDefault();
                        filterDrawer.classList.remove('active');
                        document.body.style.overflow = '';
                    });
                }

                // Close drawer when clicking on overlay
                filterDrawer.addEventListener('click', (event) => {
                    if (event.target === filterDrawer) {
                        filterDrawer.classList.remove('active');
                        document.body.style.overflow = '';
                    }
                });

                // Handle ESC key to close drawer
                document.addEventListener('keydown', (event) => {
                    if (event.key === 'Escape' && filterDrawer.classList.contains('active')) {
                        filterDrawer.classList.remove('active');
                        document.body.style.overflow = '';
                    }
                });
            }

            // Get the form// form reset button should uncheck all inputs
            const form = this.querySelector('form#collection-filters');
            const formResetButton = this.querySelector('[type="reset"]');
            if (form) {
                if (formResetButton) {
                    formResetButton.addEventListener('click', (e) => {
                        e.preventDefault();
                        const inputs = this.querySelectorAll('input');
                        inputs.forEach(input => {
                            input.checked = false;
                        });
                        // form trigger change event
                        form.dispatchEvent(new Event('input'));
                        // Update POUCHTECH visibility after reset
                        this.updatePouchTechVisibility();
                    });
                }
                const formSubmitButton = this.querySelector('[type="submit"]');
                if (formSubmitButton) {
                    formSubmitButton.addEventListener('click', (e) => {
                        e.preventDefault();
                        filterDrawer.classList.remove('active');
                        document.body.style.overflow = '';
                    });
                }
                // Add loading class to improve UX
                form.addEventListener('input', () => {
                    // Add loading state to product-grid to increase performance perception
                    this.querySelector('.product-grid-collection-wrapper').classList.add('loading');
                    // Update POUCHTECH filter visibility on input change
                    this.updatePouchTechVisibility();
                    // Debounce the URL update to wait until user has stopped changing inputs for 1 second
                    this.debounce(() => {
                        // set the window location query string params to include the form data
                        const formData = new FormData(form);
                        const urlParams = new URLSearchParams(formData);
                        const newUrl = `${window.location.origin}${window.location.pathname}?${urlParams.toString()}`;

                        // Disable all filter inputs and buttons
                        const filterInputs = form.querySelectorAll('input, button');
                        filterInputs.forEach(input => {
                            input.disabled = true;
                        });

                        // Update the URL
                        // Add to history (enable filter by navigation buttons)
                        // window.history.pushState({ formData: true }, '', newUrl);
                        // Replace the current history entry (disable filter by navigation buttons)
                        window.history.replaceState({ formData: true }, '', newUrl);

                        // Dispatch a custom event since pushState doesn't trigger popstate
                        window.dispatchEvent(new CustomEvent('statechange', {
                            detail: { url: newUrl, formData: formData }
                        }));
                    }, 1000); // 1 second delay
                });

                // Listen for both popstate (browser back/forward) and our custom statechange event
                window.addEventListener('popstate', this.handleStateChange.bind(this));
                window.addEventListener('statechange', this.handleStateChange.bind(this));
            }
        }

        // Set up quick filter tabs to interact with category filters
        setupQuickFilterTabs() {
            const quickFilterLinks = this.querySelectorAll('.quick-filter a');
            if (quickFilterLinks.length) {
                quickFilterLinks.forEach(link => {
                    link.addEventListener('click', (e) => {
                        e.preventDefault();
                        window.history.replaceState({ formData: true }, '', e.currentTarget.href);

                        // Dispatch a custom event since pushState doesn't trigger popstate
                        window.dispatchEvent(new CustomEvent('statechange', {
                            detail: { url: e.currentTarget.href }
                        }));
                        return false;
                    });
                });
            }

            const form = this.querySelector('form.collection-filters');
            if (!form) return;
            const quickFilterTabs = this.querySelectorAll('[data-reset-filter]');
            if (quickFilterTabs.length) {
                quickFilterTabs.forEach((tab, index) => {
                    tab.addEventListener('click', () => {
                        const filterInputs = form.querySelectorAll(`input[name="${tab.dataset.resetFilter}"]:checked`);
                        filterInputs.forEach(input => {
                            input.checked = false;
                        });
                        // Trigger a change event on the form to update the products
                        form.dispatchEvent(new Event('input', { bubbles: true }));
                    });
                });
            }
        }

        disconnectedCallback() {
        }

        handleStateChange(event) {
            // Add loading state to product-grid
            this.querySelector('.product-grid-collection-wrapper').classList.add('loading');
            // Check if URL already has query parameters
            const url = new URL(window.location.href);
            url.searchParams.append('section_id', this.dataset.sectionId);

            fetch(url.toString())
                .then(response => response.text())
                .then(data => {
                    // Get #product-grid element from response
                    const parser = new DOMParser();
                    const doc = parser.parseFromString(data, 'text/html');
                    const productGrid = doc.querySelector('#ProductGridContainer');
                    const quickFilter = doc.querySelector('.quick-filter');
                    if (quickFilter) {
                        this.querySelector('.quick-filter').innerHTML = quickFilter.innerHTML;
                        this.setupQuickFilterTabs();
                    }
                    if (productGrid) {
                        this.querySelector('#ProductGridContainer').innerHTML = productGrid.innerHTML;
                        this.updateCollectionGridImages();
                    }
                    const collectionFilters = doc.querySelector('.collection-filters');
                    if (collectionFilters) {
                        this.querySelector('.collection-filters').innerHTML = collectionFilters.innerHTML;
                    }
                    const applyButton = doc.querySelector('.collection-filter-footer button[type="submit"]');
                    if (applyButton) {
                        this.querySelector('.collection-filter-footer button[type="submit"]').textContent = applyButton.textContent;
                    }
                    // Remove loading state from product-grid
                    this.querySelector('.product-grid-collection-wrapper').classList.remove('loading');

                    // Update POUCHTECH filter visibility after state change
                    this.updatePouchTechVisibility();
                }).catch(error => {
                    console.error('Error fetching product-grid:', error);
                    // Remove loading state from product-grid
                    this.querySelector('.product-grid-collection-wrapper').classList.remove('loading');
                });
        }

        setupCollectionGridImages() {
            const collectionGridImagesScript = document.getElementById('collectionGridImages');
            if (collectionGridImagesScript) {
                this.collectionGridImages = JSON.parse(collectionGridImagesScript.textContent);
                this.updateCollectionGridImages();
            }
        }

        handelize(string) {
            return string.toLowerCase().replace(/ /g, '-');
        }

        updateCollectionGridImages() {
            if (this.querySelector('#no-products-found')) {
                return;
            }
            let activeCategory = this.querySelector('.quick-filter .tab-title.active');
            if (!activeCategory || !activeCategory.dataset.collection) return;
            if (!this.collectionGridImages) return;
            const collectionGridImages = this.collectionGridImages[activeCategory.dataset.collection];
            const productCards = this.querySelectorAll('#product-grid .grid__item');
            collectionGridImages.forEach((gridImage) => {
                const featuredImageItem = document.createElement('li');
                featuredImageItem.classList.add('grid__item', 'featured-image-card');
                const featuredImage = document.createElement('img');
                featuredImage.src = gridImage.image;
                featuredImage.classList.add('featured-image');
                featuredImageItem.appendChild(featuredImage);
                // Insert the featured image at the index of the gridImage.position
                const productCard = productCards[gridImage.position - 1];
                const productGrid = this.querySelector('#product-grid');
                if (productGrid) {
                    if (productCard) {
                        productGrid.insertBefore(featuredImageItem, productCard);
                    } else {
                        // If position is beyond available cards, append to the end
                        productGrid.appendChild(featuredImageItem);
                    }
                }
            });
        }

        // Setup POUCHTECH filter behavior
        setupPouchTechFilter() {
            // Initially check POUCHTECH visibility based on default selections
            this.updatePouchTechVisibility();

            // Watch for URL changes from state events to update filter visibility
            window.addEventListener('statechange', () => {
                // Small delay to ensure DOM is updated
                setTimeout(() => this.updatePouchTechVisibility(), 50);
            });

            window.addEventListener('popstate', () => {
                // Small delay to ensure DOM is updated
                setTimeout(() => this.updatePouchTechVisibility(), 50);
            });
        }

        // Update visibility of POUCHTECH filter based on selected categories
        updatePouchTechVisibility() {
            const pouchTechFilter = this.querySelector('.collection-filter--pouchtech');
            if (!pouchTechFilter) return; // Early return if filter doesn't exist

            // Get all checked category filters
            const categoryFilters = Array.from(this.querySelectorAll('input[name="filter.p.m.custom.category"]:checked'));
            const categoryValues = categoryFilters.map(input => input.value.toLowerCase());

            // Define category values that should not show POUCHTECH when they're the only ones selected
            const nonPouchTechCategories = ['t-shirts', 'briefs', 'socks', 't-shirt', 'brief', 'sock'];

            // Logic to determine if we should show POUCHTECH
            let shouldShowPouchTech = false;

            // If no categories are selected, show POUCHTECH (all products visible)
            if (categoryValues.length === 0) {
                shouldShowPouchTech = true;
            } else {
                // Check if any selected category is NOT in our non-POUCHTECH list
                // (meaning Boxers/Trunks or another compatible category is selected)
                shouldShowPouchTech = categoryValues.some(value => {
                    // Check if value contains boxers, trunks, or is not in our exclusion list
                    return value.includes('boxer') ||
                        value.includes('trunk') ||
                        !nonPouchTechCategories.some(nonPouchCategory => value.includes(nonPouchCategory));
                });
            }

            // Show/hide the POUCHTECH filter
            if (shouldShowPouchTech) {
                pouchTechFilter.style.display = ''; // Show filter
            } else {
                // Hide filter and uncheck any selections
                pouchTechFilter.style.display = 'none';
                const pouchTechInputs = pouchTechFilter.querySelectorAll('input');
                pouchTechInputs.forEach(input => {
                    if (input.checked) {
                        input.checked = false;
                        // Trigger input event to update the URL params without removing other categories
                        input.dispatchEvent(new Event('input', { bubbles: true }));
                    }
                });
            }
        }

    });
}